import React, { FunctionComponent, useState } from "react";

import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";

import { Category } from "../../../models/Product";
import * as ProductCategoryApi from "../../../api/productCategoryApi";
import useStyles from "./styles";
import { ImageEditor } from "../../../components/editors/ImageEditor";
// @ts-ignore
import ScanditBarcodeScanner from "scandit-sdk-react";
import { ImageInformation } from "../../../models/ImageInformation";
import { isOk } from "../../../result";
import { ProblemDetails } from "../../../models/Http";
import { useSelector } from "react-redux";
import { AppState } from "../../../store";
import { Language } from "../../../models/Translation";
import { useLoadIfEmpty } from "../../../hooks/useLoadIfEmpty";
import { thunkFetchCurrentTenantLanguages } from "../../../store/tenants/actions";
import * as translationApi from "../../../api/translationApi";
import { getTranslationValuesOrDefault } from "../../../utils";
import { useI18n } from "../../../hooks/useI18n";

interface ProductCategoryEditorProps {
  category: Category;
  onSave?: () => void;
}

export const ProductCategoryEditor: FunctionComponent<
  ProductCategoryEditorProps
> = ({ category, onSave }) => {
  const classes = useStyles();
  const { t } = useI18n();

  const tenantLanguages =
    useSelector<AppState, ReadonlyArray<Language>>(
      s => s.currentTenant.languages
    ) || [];

  useLoadIfEmpty(!tenantLanguages.length, thunkFetchCurrentTenantLanguages);

  const [state, setState] = useState({
    category: category,
    image: { url: category.imageUrl } as ImageInformation,
    addingCategory: false,
    error: {} as ProblemDetails
  });

  return (
    <>
      <FormControlLabel
        style={{ marginBottom: 10 }}
        control={
          <Checkbox
            checked={state.category.isOrderable}
            onChange={e =>
              setState({
                ...state,
                category: {
                  ...state.category,
                  isOrderable: e.target.checked
                }
              })
            }
          />
        }
        label={t("common.formFields.availableForOrders")}
      />
      {tenantLanguages &&
        tenantLanguages.map((x: any) => {
          return (
            <React.Fragment key={x?.code}>
              <TextField
                className={classes.field}
                label={x?.name}
                required
                value={
                  state.category?.translation?.values[x.code]
                    ? state.category?.translation?.values[x.code]
                    : ""
                }
                onChange={e => {
                  let translationValues = getTranslationValuesOrDefault(
                    state.category?.translation
                  );
                  translationValues[x.code] = e.target.value;
                  setState({
                    ...state,
                    category: {
                      ...state.category,
                      translation: {
                        ...state.category.translation,
                        values: translationValues
                      }
                    }
                  });
                }}
              />
            </React.Fragment>
          );
        })}
      <div>
        <ImageEditor
          initial={(state.image as ImageInformation) ?? { url: "" }}
          onChange={data => setState({ ...state, image: data })}
          title={t("common.categoryManagement.image500x500px")}
        />
      </div>
      <Button
        style={{ width: 200, margin: "auto", display: "flex" }}
        variant="contained"
        color="primary"
        disabled={state.addingCategory}
        onClick={async () => {
          setState({ ...state, addingCategory: true });
          let result;

          if (state.category.id === undefined) {
            result = await ProductCategoryApi.addCategory(state.category);
          } else {
            result = await ProductCategoryApi.updateCategory(state.category);
          }

          if (!isOk(result)) {
            setState({ ...state, error: result.details });
            return;
          }

          const translationResult = await translationApi.upsert(
            result.value
              ? { ...state.category.translation, id: result.value.name }
              : state.category.translation
          );

          if (!isOk(translationResult)) {
            setState({ ...state, error: translationResult.details });
            return;
          }

          if (state.image?.blob) {
            result = await ProductCategoryApi.uploadImage(
              result.value.id,
              state.image.blob
            );

            if (!isOk(result)) {
              setState({ ...state, error: result.details });
              return;
            }
          }

          if (onSave) {
            onSave();
          }
        }}
      >
        {t("common.categoryManagement.saveCategory")}{" "}
        {state.addingCategory && (
          <CircularProgress size={20} style={{ marginLeft: 20 }} />
        )}
      </Button>
      {state.error?.title && (
        <Alert severity="error" style={{ marginTop: 10 }}>
          {`${t("common.categoryManagement.errorAddingOrUpdatingCategory")} (${
            state.error.title
          } - ${state.error.detail})`}
        </Alert>
      )}
    </>
  );
};
